import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import {
    Grid,
    Button,
    Popup,
    Icon,
    Dropdown,
    Table
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { postMassSimulation } from "../../../../actions/analytics-v2";
import { updateWorkspaceInStore } from "../../../../actions/workspace";
import { getSafeSelectedWorkspace } from "../../../../utils/mappers/user";
import { mapCurrentUserToWorkspaceOptions } from "../../../../utils/mappers/workspace";
import UploadSimulationInputsModal from "./UploadSimulationInputsModal/UploadSimulationInputsModal";

export const MassSimulation = ({
    currentWorkspaceId,
    workspaces,
    currentUser,
    updateWorkspaceInStore,
}) => {
    const { t } = useTranslation();

    const [workspaceSelected, setWorkspaceSelected] = useState(currentWorkspaceId)
    const [generatingSimulation, setGeneratingSimulation] = useState(false)
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [stagedFile, setStagedFile] = useState(null)
    const [stagedAt, setStagedAt] = useState(null)
    const [validationErrors, setValidationErrors] = useState([])

    const selectedWorkspace = workspaces.find((workspace) => workspace.id === workspaceSelected)
    const dailyRemaining = selectedWorkspace && selectedWorkspace.mass_simulation_daily_remaining !== undefined
        ? selectedWorkspace.mass_simulation_daily_remaining
        : "-"
    const monthlyRemaining = selectedWorkspace && selectedWorkspace.mass_simulation_monthly_remaining !== undefined
        ? selectedWorkspace.mass_simulation_monthly_remaining
        : "-"

    const handleWorkspaceSelectorChange = (e, selectedValue) => {
        setWorkspaceSelected(selectedValue.value)
        // A staged file was validated against the previously selected workspace's materials - it no longer applies.
        setStagedFile(null)
        setStagedAt(null)
    }

    const handleGenerateMassSimulation = async () => {
        if (generatingSimulation) return;
        setGeneratingSimulation(true);
        setValidationErrors([]);
        const result = await postMassSimulation(workspaceSelected, stagedFile);
        if (result === true && selectedWorkspace) {
            updateWorkspaceInStore({
                ...selectedWorkspace,
                mass_simulation_daily_remaining: selectedWorkspace.mass_simulation_daily_remaining - 1,
                mass_simulation_monthly_remaining: selectedWorkspace.mass_simulation_monthly_remaining - 1,
            });
        } else if (result && result.csvErrors) {
            setValidationErrors(result.csvErrors);
        }
        setGeneratingSimulation(false);
    }

    const handleFileStaged = (file, timestamp) => {
        setStagedFile(file);
        setStagedAt(timestamp);
    }

    const handleStagedFileCleared = () => {
        setStagedFile(null);
        setStagedAt(null);
    }

    return (
        <>
        <Grid.Row columns={5}>
            <Grid.Column width={3} textAlign="left" verticalAlign="bottom" flex={1}>
                <div style={{
                    display: "flex",
                    alignItems: "center"
                }}>
                    <label>
                        {t("account.adminConsole.massSimulation")}
                    </label>
                    <Popup content={t("account.adminConsole.massSimulationDisclaimer")}
                        trigger={
                            <span
                            style={{
                                marginLeft: 10,
                                backgroundColor: "#E0E1E2",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 20,
                                height: 20,
                                borderRadius: "50%"
                            }}
                            >
                                <Icon name="info" size="small" />
                            </span>
                        }
                    />
                </div>
            </Grid.Column>
            <Grid.Column width={4}>
                <label style={{ textAlign: "left" }}>{t("account.adminConsole.workspaces")}</label>
                <Dropdown
                    placeholder={t("demand.itemDescription")}
                    fluid
                    value={workspaceSelected}
                    onChange={handleWorkspaceSelectorChange}
                    disabled={generatingSimulation}
                    search selection
                    options={mapCurrentUserToWorkspaceOptions(workspaces, currentUser)}
                />
            </Grid.Column>
            <Grid.Column width={3}>
                <label style={{ textAlign: "left" }}>{t("account.adminConsole.remainingSimulations")}</label>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    padding: "7px 14px",
                    boxSizing: "border-box",
                    border: "1px solid rgba(34,36,38,.15)",
                    borderRadius: "0.5rem",
                    background: "#fff",
                }}>
                    <span style={{ textAlign: "left" }}>
                        {dailyRemaining} <span style={{ fontSize: 11 }}>{t("account.adminConsole.remainingSimulationsDaily")}</span>
                    </span>
                    <span>/</span>
                    <span style={{ textAlign: "right" }}>
                        {monthlyRemaining} <span style={{ fontSize: 11 }}>{t("account.adminConsole.remainingSimulationsMonthly")}</span>
                    </span>
                </div>
            </Grid.Column>
            <Grid.Column width={3} textAlign="left" verticalAlign="bottom">
                <Button
                    size="small"
                    className="workspace-row-left-button"
                    fluid
                    color={stagedFile ? "green" : undefined}
                    onClick={() => setUploadModalOpen(true)}
                    type="button"
                >
                    <Icon name={stagedFile ? "check circle" : "attach"} />
                    {" "}{t(stagedFile
                        ? "account.adminConsole.massSimulationInputsUploaded"
                        : "account.adminConsole.massSimulationUploadInputs")}
                </Button>
            </Grid.Column>
            <Grid.Column width={3} textAlign="left" verticalAlign="bottom">
                <Button
                    primary
                    size="small"
                    className="workspace-row-left-button"
                    fluid
                    onClick={handleGenerateMassSimulation}
                    loading={generatingSimulation}
                    disabled={generatingSimulation || !workspaceSelected || dailyRemaining <= 0 || monthlyRemaining <= 0}
                >
                    {t("account.adminConsole.generateMassSimulation")}
                </Button>
            </Grid.Column>
        </Grid.Row>
        {
            validationErrors.length > 0 &&
            <Grid.Row columns={1}>
                <Grid.Column width={16}>
                    <label className="field-title">
                        {t("account.adminConsole.massSimulationValidationErrorsTitle")}
                    </label>
                    <Table celled compact size="small">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorMaterialColumn")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorColumnColumn")}</Table.HeaderCell>
                                <Table.HeaderCell>{t("account.adminConsole.massSimulationErrorMessageColumn")}</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {
                                validationErrors.map((error, index) => (
                                    <Table.Row key={`${error.material_id}-${error.column}-${index}`}>
                                        <Table.Cell>{error.material_id || "-"}</Table.Cell>
                                        <Table.Cell>{error.column || "-"}</Table.Cell>
                                        <Table.Cell>{(error.message || []).join(" ")}</Table.Cell>
                                    </Table.Row>
                                ))
                            }
                        </Table.Body>
                    </Table>
                </Grid.Column>
            </Grid.Row>
        }
        {
            uploadModalOpen &&
            <UploadSimulationInputsModal
                currentWorkspaceId={workspaceSelected}
                stagedFile={stagedFile}
                stagedAt={stagedAt}
                onStaged={handleFileStaged}
                onCleared={handleStagedFileCleared}
                onClose={() => setUploadModalOpen(false)}
            />
        }
        </>
    );
};

MassSimulation.propTypes = {
    currentWorkspaceId: PropTypes.number,
    currentUser: PropTypes.object.isRequired,
    workspaces: PropTypes.arrayOf(PropTypes.object),
    updateWorkspaceInStore: PropTypes.func,
};

const mapStateToProps = (state) => ({
    currentWorkspaceId: getSafeSelectedWorkspace(state),
    currentUser: state.auth.user,
    workspaces: state.workspace.workspaces,
});

export default connect(mapStateToProps, {
    updateWorkspaceInStore,
})(MassSimulation);
